/**
 * リアルタイム対戦（T15）用の定数・型定義。
 *
 * Hub の WebSocket API 仕様に合わせた値を集約する。
 *
 * 参考: docs/20_API/API_WebSocket.md
 *   - `wss://api.yamanekogames.com/ws?token=<TICKET>&roomId=<ROOM_ID>`
 *   - サーバー側の再接続猶予: 30 秒 (`RECONNECT_GRACE_MS`)
 *   - サーバー側ハートビート: 30 秒間隔で ping、60 秒で切断
 *   - ws-ticket の有効期限: 30 秒
 *
 * ハードコード禁止ルール（.claude/rules/no-hardcode.md）に従い、
 * UI ラベル / タイムアウト / コマンドタイプをここに集約する。
 */

import type { Cell } from '../game/types'

// ---------------------------------------------------------------------------
// WebSocket タイミング定数
// ---------------------------------------------------------------------------

/** ws-ticket の発行から有効期限までの想定値（Hub 仕様 30 秒）。 */
export const WS_TICKET_TTL_MS = 30_000

/** 再接続試行の最大回数（指数バックオフ）。 */
export const WS_RECONNECT_MAX_ATTEMPTS = 3

/** 再接続時の初期バックオフ遅延（ms）。試行ごとに 2 倍される。 */
export const WS_RECONNECT_BASE_DELAY_MS = 1_000

/** 再接続バックオフの上限（ms）。 */
export const WS_RECONNECT_MAX_DELAY_MS = 10_000

/**
 * 異常切断時にゲームを継続可能とみなす猶予時間（ms）。
 * Hub の `RECONNECT_GRACE_MS` と一致させる。
 */
export const WS_DISCONNECT_GRACE_MS = 30_000

/**
 * クライアント側のアプリケーション ping 間隔（ms）。
 * サーバー側の WS プロトコル ping（30 秒）とは別経路で、
 * Redis の接続 TTL（120 秒）を延長することで強制切断を防ぐ。
 */
export const WS_HEARTBEAT_INTERVAL_MS = 15_000

/**
 * 手番タイムアウト（ms）。
 * サーバー権威 reducer は未使用のため、UI 側で経過時刻を計測して警告を出すのみ。
 * 将来的にサーバー側ロジックが手番を監督する設計に移行する余地を残す。
 */
export const WS_MOVE_TIMEOUT_MS = 30_000

/** WS 接続確立のタイムアウト（ms）。 */
export const WS_CONNECT_TIMEOUT_MS = 10_000

// ---------------------------------------------------------------------------
// メッセージ / コマンド種別
// ---------------------------------------------------------------------------

/**
 * Hub の WS メッセージ typ（Upstream / Downstream）を網羅する。
 * SPEC-022 の room_command / room_command_result も将来のために含める。
 */
export const REALTIME_MESSAGE_TYPES = {
  // Upstream
  Signal: 'signal',
  StateSync: 'state_sync',
  RoomCommand: 'room_command',
  Ping: 'ping',
  // Downstream
  Connected: 'connected',
  RoomCommandResult: 'room_command_result',
  PlayerJoined: 'player_joined',
  PlayerLeft: 'player_left',
  PlayerDisconnected: 'player_disconnected',
  PlayerReconnected: 'player_reconnected',
  HostChanged: 'host_changed',
  Kicked: 'kicked',
  RoomClosed: 'room_closed',
  Pong: 'pong',
  Error: 'error',
} as const

export type RealtimeMessageType =
  (typeof REALTIME_MESSAGE_TYPES)[keyof typeof REALTIME_MESSAGE_TYPES]

/**
 * ゲーム固有のアプリケーションコマンド名。
 * WS `signal` / `state_sync` の payload.commandType として使う。
 * （Hub の Reducer に登録していなくても signal broadcast で到達する）
 */
export const REALTIME_COMMAND_TYPES = {
  /** 着手を通知する（通常プレイ中）。 */
  PlaceStone: 'place_stone',
  /** ホスト（先手決定者）が対戦開始を通知する。 */
  StartMatch: 'start_match',
  /** 再接続直後に最新 state の送信を要請する。 */
  RequestState: 'request_state',
  /** RequestState への応答として現在の state を返す。 */
  StateSnapshot: 'state_snapshot',
  /** 投了を通知する。 */
  Forfeit: 'forfeit',
} as const

export type RealtimeCommandType =
  (typeof REALTIME_COMMAND_TYPES)[keyof typeof REALTIME_COMMAND_TYPES]

// ---------------------------------------------------------------------------
// WebSocket Close Codes（Hub 仕様）
// ---------------------------------------------------------------------------

export const WS_CLOSE_CODES = {
  Normal: 1000,
  GoingAway: 1001,
  Unauthorized: 4001,
  Forbidden: 4003,
  RoomNotFound: 4004,
  DuplicateConnection: 4008,
  Kicked: 4009,
  RateLimited: 4029,
  InternalError: 4500,
} as const

// ---------------------------------------------------------------------------
// ランタイム型
// ---------------------------------------------------------------------------

/** Hub から受信する WebSocket メッセージ（Downstream 共通形）。 */
export interface RealtimeMessage<TPayload = unknown> {
  type: string
  roomId?: string
  from?: string
  to?: string
  payload?: TPayload
  ts?: number
  msgId?: string
}

/** 自前で保持する接続フェーズ。 */
export type RealtimeConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'closed'
  | 'error'

/** 対戦結果の終了理由。 */
export type BattleResultReason =
  | 'win'
  | 'lose'
  | 'draw'
  | 'forfeit'
  | 'opponent_forfeit'
  | 'connection_lost'
  | 'exited'

/** ロビー/対戦画面が親コンポーネントに戻すときの結果。 */
export interface BattleResult {
  reason: BattleResultReason
  /** 自分が勝者なら true。draw / connection_lost / exited では null。 */
  playerWon: boolean | null
  /** 最終盤面スナップショット（リザルト表示用）。 */
  snapshot: BattleSnapshot | null
}

/** メンバー（自分 + 相手）の対戦情報。 */
export interface BattleMember {
  userId: string
  color: 'black' | 'white'
  displayName: string
  avatarUrl: string | null
  online: boolean
}

/**
 * リアルタイム対戦の「論理状態」スナップショット。
 * engine.ts の純粋関数で盤面から都度 computeScore するため、
 * ここには盤面 / 手番 / 終了フラグのみを保持する。
 */
export interface BattleSnapshot {
  board: Cell[][]
  turn: 'black' | 'white'
  turnStartedAt: number
  turnCount: number
  gameOver: boolean
  winner: 'black' | 'white' | 'draw' | null
  members: BattleMember[]
  /** 直近の着手セル（演出用）。 */
  lastMove: { row: number; col: number; color: 'black' | 'white' } | null
}

/** WS メッセージに付けるゲームデータの基底型。 */
export interface RealtimeCommandEnvelope<TPayload = unknown> {
  commandType: RealtimeCommandType
  payload: TPayload
  /** 冪等性確保用の一意 ID（SDK に相当しない場合はクライアント側で uuid を生成）。 */
  requestId: string
}

// ---------------------------------------------------------------------------
// UI ラベル
// ---------------------------------------------------------------------------

export const REALTIME_UI = {
  connecting: '対戦相手と接続しています…',
  waitingOpponent: '相手の入室を待っています…',
  yourTurn: 'あなたのターン',
  opponentTurn: '相手のターン…',
  reconnecting: '接続が切れました。再接続中…',
  connectionLostTitle: '相手との接続が切れました',
  connectionLostMessage: '時間内に再接続できませんでした。タイトルに戻ります。',
  forfeitConfirm: '対戦を投了してタイトルに戻りますか？',
  exitButton: '退出',
  retryButton: 'もう一度',
  cancelButton: 'キャンセル',
  moveTimeoutWarning: '時間切れです',
  winTitle: '勝利！',
  loseTitle: '敗北…',
  drawTitle: '引き分け',
  opponentForfeitTitle: '相手が投了しました',
  connectErrorTitle: '接続に失敗しました',
} as const
